import React, { useEffect, useMemo, useState } from 'react';
import { hikingStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { ratingStrings } from '../../content/ratings';
import { validateHikingForm } from '../../validation/hobbies';
import ChecklistTemplateTools from '../checklists/ChecklistTemplateTools';
import PhotoAlbumsSection from '../entryPhotos/PhotoAlbumsSection';
import TripHikePhotoPicker from '../entryPhotos/TripHikePhotoPicker';
import EntryPhotoAlbumDraftPicker from '../entryPhotos/EntryPhotoAlbumDraftPicker';
import { emptyAlbumDraft, validateAlbumDraft } from '../entryPhotos/albumDraft';
import useStepperFlow from '../../hooks/useStepperFlow';
import { showFirstStepWithErrors } from '../../utils/formStepErrors';

/**
 * Performs the today operation.
 *
 * @returns {void} No value; the operation applies its documented side effects.
 */
const today = () => new Date().toISOString().slice(0, 10);
const stepErrorFields = {
  details: [
    'title',
    'location',
    'hikeDate',
    'startTime',
    'status',
    'difficulty',
    'trailType',
    'terrain',
    'crowdLevel',
    'distance',
    'distanceUnit',
    'elevationGain',
    'elevationUnit',
    'durationMinutes',
    'latitude',
    'longitude',
    'routeUrl',
    'mapUrl',
    'geometrySummary',
    'tags',
  ],
  checklist: ['checklist'],
  review: ['actualDurationMinutes', 'rating', 'review', 'notes'],
};

/**
 * Performs the checklist key operation.
 *
 * @param {*} item - item value.
 * @returns {*} The derived operation result.
 */
function checklistKey(item) {
  return String(item.title || '').trim().toLowerCase();
}

/**
 * Renders the star rating experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.value - Value to inspect or transform.
 * @param {*} props.onChange - Callback invoked when change occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function StarRating({ value, onChange }) {
  const rating = Number(value || 0);
  const stars = Array.from({ length: 5 }, (_, index) => index + 1);
  /**
   * Retrieves star state.
   *
   * @param {*} star - star value.
   * @returns {*} The derived operation result.
   */
  const getStarState = (star) => {
    if (rating >= star) return 'full';
    if (rating >= star - 0.5) return 'half';
    return 'empty';
  };

  return (
    <div className="star-rating-wrap">
      <div className="star-rating" role="radiogroup" aria-label={hikingStrings.fields.rating}>
        {stars.map((star) => (
          <span className={`star-button ${getStarState(star)}`} key={star}>
            <span className="star-icon" aria-hidden="true">★</span>
            <button
              type="button"
              className="star-hit star-hit-left"
              onClick={() => onChange(String(star - 0.5))}
              aria-pressed={rating === star - 0.5}
              aria-label={`${star - 0.5} stars`}
            />
            <button
              type="button"
              className="star-hit star-hit-right"
              onClick={() => onChange(String(star))}
              aria-pressed={rating === star}
              aria-label={`${star} stars`}
            />
          </span>
        ))}
      </div>
      <div className="star-rating-summary">
        <span>{rating ? ratingStrings.value(rating) : ratingStrings.notRated}</span>
        {rating > 0 && (
          <button type="button" className="btn-ghost compact-btn" onClick={() => onChange('')}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Determines whether coordinates.
 *
 * @param {*} form - form value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function hasCoordinates(form) {
  return Number.isFinite(Number(form.latitude)) && Number.isFinite(Number(form.longitude));
}

/**
 * Renders the hike map preview experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.latitude - latitude value.
 * @param {*} props.longitude - longitude value.
 * @param {*} props.title - title value.
 * @returns {JSX.Element} The rendered interface.
 */
function HikeMapPreview({ latitude, longitude, title }) {
  const lat = Number(latitude);
  const lon = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  const delta = 0.025;
  const bbox = [lon - delta, lat - delta, lon + delta, lat + delta].join(',');
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(`${lat},${lon}`)}`;

  return (
    <div className="hike-map-preview" aria-label={`${title || 'Selected trail'} map preview`}>
      <iframe title={`${title || 'Selected trail'} map preview`} src={src} loading="lazy" />
    </div>
  );
}

const emptyHike = {
  id: null,
  title: '',
  location: '',
  hikeDate: today(),
  status: 'Want to hike',
  distance: '',
  distanceUnit: 'mi',
  elevationGain: '',
  elevationUnit: 'ft',
  durationMinutes: '',
  difficulty: 'Moderate',
  trailType: 'Loop',
  terrain: '',
  latitude: null,
  longitude: null,
  hasRouteGeometry: false,
  geometrySummary: '',
  routeUrl: '',
  mapUrl: '',
  permitRequired: false,
  dogFriendly: false,
  kidFriendly: false,
  rating: '',
  startTime: '',
  parkingNotes: '',
  companions: '',
  weather: '',
  trailCondition: '',
  crowdLevel: '',
  actualDistance: '',
  actualDurationMinutes: '',
  notes: '',
  tags: '',
  checklist: [{ id: 'checklist_1', title: '', packed: false }],
  selectedTrailName: '',
  tripId: '',
  tripTitle: '',
  tripDayId: '',
  tripDayTitle: '',
  trailCatalogId: '',
  trailSource: '',
  trailSourceId: '',
  coverPhotoUrl: '',
  coverPhotoUpdatedAt: '',
  favorite: false,
};

/**
 * Creates initial form.
 *
 * @param {*} initialData - initial data value.
 * @returns {*} The derived operation result.
 */
function createInitialForm(initialData) {
  if (!initialData) return { ...emptyHike };
  return {
    ...emptyHike,
    ...initialData,
    elevationGain: initialData.elevationGain || '',
    durationMinutes: initialData.durationMinutes || '',
    actualDurationMinutes: initialData.actualDurationMinutes || '',
    rating: initialData.rating || '',
    tags: Array.isArray(initialData.tags) ? initialData.tags.join(', ') : initialData.tags || '',
    checklist: Array.isArray(initialData.checklist) && initialData.checklist.length
      ? initialData.checklist
      : [{ id: 'checklist_1', title: '', packed: false }],
    selectedTrailName: initialData.selectedTrailName || '',
  };
}

/** Manages the hike stepper and merges selected-trail metadata with user-authored details. */
export default function HikingForm({ theme, onSave, onCancel, isSaving, initialData, onDirtyChange, isEditing = false, isEnrichingTrail = false, onLimitReached }) {
  const initialForm = useMemo(() => createInitialForm(initialData), [initialData]);
  const [form, setForm] = useState(initialForm);
  const [albumDraft, setAlbumDraft] = useState(emptyAlbumDraft);
  const [highlightDrafts, setHighlightDrafts] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [errors, setErrors] = useState({});
  const steps = useMemo(() => ['details', 'checklist', 'review'], []);
  const {
    activeSection,
    activeStepIndex,
    canGoBack,
    canGoNext,
    isStepSettled,
    goToSection,
    goToPreviousStep,
    goToNextStep,
    resetStepper,
  } = useStepperFlow(steps, 'details');

  useEffect(() => {
    setForm(initialForm);
    resetStepper('details');
    setPhotoDraft((current) => {
      if (current.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return { file: null, previewUrl: '', remove: false };
    });
    setAlbumDraft(emptyAlbumDraft);
    setHighlightDrafts([]);
    setCoverIndex(0);
  }, [initialForm, resetStepper]);

  useEffect(() => {
    onDirtyChange?.(JSON.stringify(form) !== JSON.stringify(initialForm) || Boolean(albumDraft.label || albumDraft.url) || highlightDrafts.length > 0);
  }, [form, initialForm, onDirtyChange, albumDraft, highlightDrafts]);

  /**
   * Updates field.
   *
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  /**
   * Updates checklist item.
   *
   * @param {string} id - Stable identifier of the target resource.
   * @param {*} patch - patch value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateChecklistItem = (id, patch) => {
    setForm((current) => ({
      ...current,
      checklist: current.checklist.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  };

  /**
   * Creates checklist item.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const addChecklistItem = () => {
    setForm((current) => ({
      ...current,
      checklist: [...current.checklist, { id: `checklist_${Date.now()}`, title: '', packed: false }],
    }));
  };

  /**
   * Removes checklist item.
   *
   * @param {string} id - Stable identifier of the target resource.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const removeChecklistItem = (id) => {
    setForm((current) => ({
      ...current,
      checklist: current.checklist.filter((item) => item.id !== id).length
        ? current.checklist.filter((item) => item.id !== id)
        : [{ id: `checklist_${Date.now()}`, title: '', packed: false }],
    }));
  };

  /**
   * Performs checklist template.
   *
   * @param {Array<*>} items - items collection.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const applyChecklistTemplate = (items) => {
    setForm((current) => {
      const nonEmptyExistingItems = (current.checklist || []).filter((item) => checklistKey(item));
      const existingKeys = new Set(nonEmptyExistingItems.map(checklistKey));
      const nextItems = (items || [])
        .filter((item) => item.title && !existingKeys.has(checklistKey(item)))
        .map((item) => ({
          id: `checklist_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          title: item.title,
          packed: false,
        }));

      const checklist = [...nonEmptyExistingItems, ...nextItems];
      return { ...current, checklist: checklist.length ? checklist : [{ id: `checklist_${Date.now()}`, title: '', packed: false }] };
    });
  };

  /**
   * Handles submit.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (canGoNext) {
      goToNextStep();
      return;
    }
    if (!isStepSettled) return;
    const validation = validateHikingForm(form);
    setErrors(validation.fieldErrors);
    if (!validation.valid) {
      showFirstStepWithErrors(validation.fieldErrors, stepErrorFields, 'details', goToSection);
      return;
    }
    if (!validateAlbumDraft(albumDraft)) {
      goToSection('review');
      return;
    }
    await onSave({
      ...form,
      coverPhotoFile: isEditing ? null : highlightDrafts[coverIndex] || null,
      shouldRemoveCoverPhoto: false,
      albumDraft,
      highlightDrafts: isEditing ? [] : highlightDrafts.filter((_, index) => index !== coverIndex),
    });
  };

  return (
    <form className="recipe-form hiking-form" onSubmit={(event) => event.preventDefault()} noValidate>
      <div className="hobby-flow-stepper" role="tablist" aria-label="Hike editor steps">
        {steps.map((section, index) => (
          <button
            key={section}
            type="button"
            className={activeSection === section ? 'active' : index < activeStepIndex ? 'complete' : ''}
            onClick={() => goToSection(section)}
            role="tab"
            aria-selected={activeSection === section}
          >
            <span>{index + 1}</span>
            {hikingStrings.form.sections[section]}
          </button>
        ))}
      </div>

      {activeSection === 'details' && (
        <section className="recipe-form-section priority-section">
          <div className="trip-builder-list">
            <div className="trip-builder-card">
              <div className="section-heading compact-heading">
                <div>
                  <h3 className="accent-section-title">{hikingStrings.form.sections.hike}</h3>
                </div>
              </div>
              <div className="form-grid three-columns">
                {form.selectedTrailName && (
                  <div className="form-group form-grid-full">
                    <label>{hikingStrings.fields.title}</label>
                    <input value={form.selectedTrailName} readOnly aria-readonly="true" className="locked-field-input" />
                  </div>
                )}
                {!form.selectedTrailName && (
                  <div className="form-group">
                    <label>{hikingStrings.fields.title}</label>
                    <input value={form.title} onChange={(event) => updateField('title', event.target.value)} placeholder={hikingStrings.placeholders.title} aria-invalid={Boolean(errors.title)} disabled={isEditing} className={isEditing ? 'locked-field-input' : ''} />
                    {errors.title && <p className="field-error">{errors.title}</p>}
                  </div>
                )}
                <div className="form-group">
                  <label>{hikingStrings.fields.location}</label>
                  <input value={form.location} onChange={(event) => updateField('location', event.target.value)} placeholder={hikingStrings.placeholders.location} aria-invalid={Boolean(errors.location)} />
                  {errors.location && <p className="field-error">{errors.location}</p>}
                </div>
                <div className="form-group">
                  <label>{hikingStrings.fields.date}</label>
                  <input type="date" value={form.hikeDate} onChange={(event) => updateField('hikeDate', event.target.value)} aria-invalid={Boolean(errors.hikeDate)} />
                  {errors.hikeDate && <p className="field-error">{errors.hikeDate}</p>}
                </div>
                <div className="form-group">
                  <label>{hikingStrings.fields.startTime}</label>
                  <input type="time" value={form.startTime} onChange={(event) => updateField('startTime', event.target.value)} />
                </div>
                <div className="form-group">
                  <label>{hikingStrings.fields.status}</label>
                  <select value={form.status} onChange={(event) => updateField('status', event.target.value)} aria-invalid={Boolean(errors.status)}>
                    {hikingStrings.statusOptions.map((status) => <option key={status}>{status}</option>)}
                  </select>
                  {errors.status && <p className="field-error">{errors.status}</p>}
                </div>
                <div className="form-group form-grid-full">
                  <button
                    type="button"
                    className={`favorite-toggle inline-favorite-toggle ${form.favorite ? 'active' : ''}`}
                    onClick={() => updateField('favorite', !form.favorite)}
                    aria-pressed={form.favorite}
                  >
                    <span>{form.favorite ? hikingStrings.form.favorite : hikingStrings.form.favoriteAction}</span>
                  </button>
                </div>
              </div>
            </div>

          {hasCoordinates(form) && (
            <div className="trip-builder-card">
              <div className="section-heading compact-heading">
                <div>
                  <h3 className="accent-section-title">{hikingStrings.form.sections.map}</h3>
                </div>
              </div>
              <HikeMapPreview latitude={form.latitude} longitude={form.longitude} title={form.title || form.selectedTrailName} />
            </div>
          )}

          <div className="trip-builder-card">
            <div className="section-heading compact-heading">
              <div>
                <h3 className="accent-section-title">{hikingStrings.form.sections.trailProfile}</h3>
              </div>
            </div>
            <div className={`form-grid three-columns ${isEnrichingTrail ? 'field-level-glimmer' : ''}`}>
              <div className="form-group">
                <label>{hikingStrings.fields.difficulty}</label>
                <select value={form.difficulty} onChange={(event) => updateField('difficulty', event.target.value)} aria-invalid={Boolean(errors.difficulty)}>
                  {hikingStrings.difficultyOptions.map((difficulty) => <option key={difficulty}>{difficulty}</option>)}
                </select>
                {errors.difficulty && <p className="field-error">{errors.difficulty}</p>}
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.trailType}</label>
                <select value={form.trailType} onChange={(event) => updateField('trailType', event.target.value)} aria-invalid={Boolean(errors.trailType)}>
                  {hikingStrings.trailTypeOptions.map((trailType) => <option key={trailType}>{trailType}</option>)}
                </select>
                {errors.trailType && <p className="field-error">{errors.trailType}</p>}
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.terrain}</label>
                <select value={form.terrain} onChange={(event) => updateField('terrain', event.target.value)} aria-invalid={Boolean(errors.terrain)}>
                  <option value="">Not set</option>
                  {hikingStrings.terrainOptions.map((terrain) => <option key={terrain}>{terrain}</option>)}
                </select>
                {errors.terrain && <p className="field-error">{errors.terrain}</p>}
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.distance}</label>
                <input value={form.distance} onChange={(event) => updateField('distance', event.target.value)} placeholder={hikingStrings.placeholders.distance} />
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.distanceUnit}</label>
                <select value={form.distanceUnit} onChange={(event) => updateField('distanceUnit', event.target.value)} aria-invalid={Boolean(errors.distanceUnit)}>
                  {hikingStrings.distanceUnits.map((unit) => <option key={unit}>{unit}</option>)}
                </select>
                {errors.distanceUnit && <p className="field-error">{errors.distanceUnit}</p>}
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.elevationGain}</label>
                <input type="number" min="0" value={form.elevationGain} onChange={(event) => updateField('elevationGain', event.target.value)} placeholder={hikingStrings.placeholders.elevationGain} aria-invalid={Boolean(errors.elevationGain)} />
                {errors.elevationGain && <p className="field-error">{errors.elevationGain}</p>}
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.elevationUnit}</label>
                <select value={form.elevationUnit} onChange={(event) => updateField('elevationUnit', event.target.value)} aria-invalid={Boolean(errors.elevationUnit)}>
                  {hikingStrings.elevationUnits.map((unit) => <option key={unit}>{unit}</option>)}
                </select>
                {errors.elevationUnit && <p className="field-error">{errors.elevationUnit}</p>}
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.duration}</label>
                <input type="number" min="0" value={form.durationMinutes} onChange={(event) => updateField('durationMinutes', event.target.value)} placeholder={hikingStrings.placeholders.duration} aria-invalid={Boolean(errors.durationMinutes)} />
                {errors.durationMinutes && <p className="field-error">{errors.durationMinutes}</p>}
              </div>
              <label className="inline-checkbox"><input type="checkbox" checked={form.permitRequired} onChange={(event) => updateField('permitRequired', event.target.checked)} /> {hikingStrings.fields.permitRequired}</label>
              <label className="inline-checkbox"><input type="checkbox" checked={form.dogFriendly} onChange={(event) => updateField('dogFriendly', event.target.checked)} /> {hikingStrings.fields.dogFriendly}</label>
              <label className="inline-checkbox"><input type="checkbox" checked={form.kidFriendly} onChange={(event) => updateField('kidFriendly', event.target.checked)} /> {hikingStrings.fields.kidFriendly}</label>
            </div>
          </div>

          <div className="trip-builder-card">
            <div className="section-heading compact-heading">
              <div>
                <h3 className="accent-section-title">{hikingStrings.form.sections.conditions}</h3>
              </div>
            </div>
            <div className="form-grid three-columns">
              <div className="form-group">
                <label>{hikingStrings.fields.actualDistance}</label>
                <input value={form.actualDistance} onChange={(event) => updateField('actualDistance', event.target.value)} placeholder={hikingStrings.placeholders.actualDistance} />
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.actualDuration}</label>
                <input type="number" min="0" value={form.actualDurationMinutes} onChange={(event) => updateField('actualDurationMinutes', event.target.value)} placeholder={hikingStrings.placeholders.actualDuration} aria-invalid={Boolean(errors.actualDurationMinutes)} />
                {errors.actualDurationMinutes && <p className="field-error">{errors.actualDurationMinutes}</p>}
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.weather}</label>
                <input value={form.weather} onChange={(event) => updateField('weather', event.target.value)} placeholder={hikingStrings.placeholders.weather} />
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.trailCondition}</label>
                <input value={form.trailCondition} onChange={(event) => updateField('trailCondition', event.target.value)} placeholder={hikingStrings.placeholders.trailCondition} />
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.crowdLevel}</label>
                <select value={form.crowdLevel} onChange={(event) => updateField('crowdLevel', event.target.value)} aria-invalid={Boolean(errors.crowdLevel)}>
                  <option value="">Not set</option>
                  {hikingStrings.crowdOptions.map((crowdLevel) => <option key={crowdLevel}>{crowdLevel}</option>)}
                </select>
                {errors.crowdLevel && <p className="field-error">{errors.crowdLevel}</p>}
              </div>
              <div className="form-group form-grid-full">
                <label>{hikingStrings.fields.parkingNotes}</label>
                <input value={form.parkingNotes} onChange={(event) => updateField('parkingNotes', event.target.value)} placeholder={hikingStrings.placeholders.parkingNotes} />
              </div>
              <div className="form-group">
                <label>{hikingStrings.fields.companions}</label>
                <input value={form.companions} onChange={(event) => updateField('companions', event.target.value)} placeholder={hikingStrings.placeholders.companions} />
              </div>
            </div>
          </div>

          <div className="trip-builder-card">
            <div className="section-heading compact-heading">
              <div>
                <h3 className="accent-section-title">{hikingStrings.fields.tags}</h3>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group form-grid-full">
                <label>{hikingStrings.fields.tags}</label>
                <input value={form.tags} onChange={(event) => updateField('tags', event.target.value)} placeholder={hikingStrings.placeholders.tags} />
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {activeSection === 'checklist' && (
      <section className="recipe-form-section priority-section">
        <div className="section-heading compact-heading">
          <div>
            <h3 className="accent-section-title">{hikingStrings.form.sections.checklist}</h3>
          </div>
          <div className="checklist-header-actions">
            <ChecklistTemplateTools
              hobbyId="hiking"
              hobbyName="Hiking"
              currentItems={form.checklist}
              defaultName={`${form.title || 'Hiking'} checklist`}
              onApplyItems={applyChecklistTemplate}
              buildTemplateItems={(items) => items.map((item) => ({ title: item.title, category: '' }))}
            />
            <button type="button" className="btn-secondary" onClick={addChecklistItem}>{hikingStrings.form.addChecklistItem}</button>
          </div>
        </div>
        <div className="trip-builder-list">
          {form.checklist.map((item) => (
            <div className="trip-builder-card compact-row checklist-item-row" key={item.id}>
              <div className="form-group">
                <label>{hikingStrings.fields.checklistItem}</label>
                <input value={item.title} onChange={(event) => updateChecklistItem(item.id, { title: event.target.value })} placeholder={hikingStrings.placeholders.checklistItem} />
              </div>
              <label className="inline-checkbox checklist-packed-toggle">
                <input type="checkbox" checked={item.packed} onChange={(event) => updateChecklistItem(item.id, { packed: event.target.checked })} />
                <span>{hikingStrings.fields.packed}</span>
              </label>
              <button
                type="button"
                className="row-remove-btn icon-only"
                aria-label={moduleCommonStrings.actions.remove}
                title={moduleCommonStrings.actions.remove}
                onClick={() => removeChecklistItem(item.id)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </section>
      )}

      {activeSection === 'review' && (
      <section className="recipe-form-section priority-section">
        <div className="section-heading compact-heading">
          <div>
            <h3 className="accent-section-title">{hikingStrings.form.sections.review}</h3>
          </div>
        </div>
        {!isEditing && <div className="form-grid two-columns">
          <div className="form-grid-full"><TripHikePhotoPicker files={highlightDrafts} onChange={setHighlightDrafts} coverIndex={coverIndex} onCoverIndexChange={setCoverIndex} /></div>
          <div className="form-grid-full"><EntryPhotoAlbumDraftPicker draft={albumDraft} onChange={setAlbumDraft} hobbyId="hiking" /></div>
        </div>}
        {isEditing && initialData?.id && (
          <PhotoAlbumsSection hobbyId="hiking" entryId={initialData.id} editor coverPhotoUrl={initialData.coverPhotoUrl} onLimitReached={onLimitReached} />
        )}
        <div className="form-grid two-columns">
          <div className="form-group form-grid-full">
            <label>{hikingStrings.fields.rating}</label>
            <StarRating value={form.rating} onChange={(value) => updateField('rating', value)} />
            {errors.rating && <p className="field-error">{errors.rating}</p>}
          </div>
          <div className="form-group form-grid-full">
            <label>{hikingStrings.fields.review}</label>
            <textarea rows="5" value={form.notes} onChange={(event) => updateField('notes', event.target.value)} />
          </div>
        </div>
      </section>
      )}

      <div className="form-actions sticky-actions">
        <button type="button" className="btn-ghost" onClick={() => onCancel({ confirmDiscard: true })}>{moduleCommonStrings.actions.cancel}</button>
        <div className="stepper-actions">
          {canGoBack && (
            <button type="button" className="btn-secondary" onClick={goToPreviousStep}>
              {moduleCommonStrings.actions.back}
            </button>
          )}
          {canGoNext ? (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={goToNextStep}>
              {moduleCommonStrings.actions.next}
            </button>
          ) : (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={handleSubmit} disabled={isSaving || !isStepSettled}>
              {isSaving ? moduleCommonStrings.actions.saving : hikingStrings.form.save}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
